import { useAppSelector } from 'redux-stores';
import { useGetTasksQuery } from 'redux-stores/tasks/tasksApi';
import { ALL_MESSAGES } from 'redux-stores/tasks/tasksSlice';
import type { Task } from 'js-sdk/src/types/Tasks';

export type MessageTaskState = {
  /** The task linked to this message, if any. */
  task: Task | undefined;
  /** Whether a task already exists for this message (drives affordance state). */
  hasTask: boolean;
  /** True while the org-wide or conversation-scoped tasks fetch is in flight. */
  isLoading: boolean;
};

/**
 * Returns the task linked to a message. Session-local mutations (create/
 * complete/reopen/update) come from linkedTasksByMessageId; org-wide data is
 * read from RTK Query directly.
 */
export function useMessageTask(
  messageId: string,
  organizationId: string,
  enabled: boolean
): MessageTaskState {
  const { data: openTasks, isLoading: isOpenLoading } = useGetTasksQuery(
    { organizationId, filter: 'open' },
    { skip: !enabled }
  );
  const { data: completedTasks, isLoading: isCompletedLoading } = useGetTasksQuery(
    { organizationId, filter: 'completed' },
    { skip: !enabled }
  );
  const isConversationLoading = useAppSelector((state) => {
    const { activeConversationKey, pendingConversationFetchesByKey } = state.tasks;
    const pending = activeConversationKey && pendingConversationFetchesByKey[activeConversationKey];
    return !!pending && !!(pending[ALL_MESSAGES] || pending[messageId]);
  });
  const isLoading = isOpenLoading || isCompletedLoading || isConversationLoading;

  // Prefer session-local task (latest mutation), then conversation-scoped
  // tasks (POST /tasks/messages), then the org-wide fetched list.
  const sessionTask = useAppSelector((state) => state.tasks.linkedTasksByMessageId[messageId]);
  const conversationTask = useAppSelector((state) => {
    const { activeConversationKey, conversationTasksByKey } = state.tasks;
    return activeConversationKey
      ? conversationTasksByKey[activeConversationKey]?.[messageId]?.[0]
      : undefined;
  });
  const fetchedTask = [...(openTasks ?? []), ...(completedTasks ?? [])].find(
    (t) => t.source_type === 'message' && t.source_ref?.message_id === messageId
  );
  const task = sessionTask ?? conversationTask ?? fetchedTask;

  return { task, hasTask: !!task, isLoading };
}
